/**
 * Integrations → Printers (`?tab=printers`, ClickUp z8nrz7evkc): label printer
 * setup, moved out of old database tables into screens a site admin can use.
 * Setup only: printing a real product label from it is the next ticket.
 *
 * The Printer association table, then four lists under two headings, driven by
 * `?printers=` so each is deep-linkable. The switch is a segmented control
 * (role="group" with aria-pressed buttons) copied from SimulationPanel's
 * ModeSwitch, NOT a nested <Tabs>: a second role="tabpanel" in the DOM would
 * break the unscoped `getByRole('tabpanel')` locators in the e2e helpers.
 *
 * Everyone who can open the page sees the lists. Adding, editing, approving,
 * retiring, test printing and changing a label type's printer type need
 * `label_printing.manage`.
 */
import { useState } from 'react'
import { Printer } from 'lucide-react'
import { useLabelPrintingOptions } from '@/hooks/useLabelPrinting'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import { cn } from '@/lib/utils'
import { IsbtPrinterList } from './IsbtPrinterList'
import { IsbtTemplatesList } from './IsbtTemplatesList'
import { LabelTypesList } from './LabelTypesList'
import { StatusBanner } from './shared'
import { ZebraPrintersList } from './ZebraPrintersList'
import { ZebraTemplatesList } from './ZebraTemplatesList'

export const PRINTERS_VALUES = [
  'printer-association',
  'isbt-templates',
  'isbt-printers',
  'zebra-templates',
  'zebra-printers',
] as const
export type PrintersView = (typeof PRINTERS_VALUES)[number]

export function isPrintersView(value: string | null): value is PrintersView {
  return value !== null && (PRINTERS_VALUES as readonly string[]).includes(value)
}

const VIEW_LABEL: Record<PrintersView, string> = {
  'printer-association': 'Printer association',
  'isbt-templates': 'ISBT Templates',
  'isbt-printers': 'ISBT Printers',
  'zebra-templates': 'Zebra Templates',
  'zebra-printers': 'Zebra Printers',
}

const GROUPS: { label: string; views: PrintersView[] }[] = [
  { label: 'Labels', views: ['printer-association'] },
  { label: 'ISBT · HemaTrax / NiceLabel', views: ['isbt-templates', 'isbt-printers'] },
  { label: 'Zebra · ZPL', views: ['zebra-templates', 'zebra-printers'] },
]

export function PrintersPanel({
  view,
  onViewChange,
}: {
  view: PrintersView
  onViewChange: (view: PrintersView) => void
}) {
  const { can } = usePermission()
  const canManage = can(PERMISSIONS.labelPrintingManage)
  // Fetched here so the sizes are cached before any dialog opens; the dialogs
  // read the same query and fill a new draft's defaults from it.
  useLabelPrintingOptions()
  // The last save or approval, until the user moves to another list.
  const [notice, setNotice] = useState<string | null>(null)

  function changeView(next: PrintersView) {
    setNotice(null)
    onViewChange(next)
  }

  return (
    <div className='space-y-5'>
      <div className='flex flex-wrap items-center gap-2.5'>
        <div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary'>
          <Printer className='h-5 w-5' />
        </div>
        <div>
          <h3 className='text-sm font-semibold leading-tight'>Label printers</h3>
          <p className='text-xs text-muted-foreground'>Label designs and the printers that use them.</p>
        </div>
      </div>

      <div className='flex flex-wrap gap-6'>
        {GROUPS.map(g => (
          <ViewSwitch key={g.label} label={g.label} views={g.views} view={view} onViewChange={changeView} />
        ))}
      </div>

      {notice && <StatusBanner tone='ok'>{notice}</StatusBanner>}

      {/* Only the chosen list is mounted. */}
      {view === 'printer-association' && <LabelTypesList canManage={canManage} onNotice={setNotice} />}
      {view === 'isbt-templates' && <IsbtTemplatesList canManage={canManage} onNotice={setNotice} />}
      {view === 'isbt-printers' && <IsbtPrinterList canManage={canManage} onNotice={setNotice} />}
      {view === 'zebra-templates' && <ZebraTemplatesList canManage={canManage} onNotice={setNotice} />}
      {view === 'zebra-printers' && <ZebraPrintersList canManage={canManage} onNotice={setNotice} />}
    </div>
  )
}

function ViewSwitch({
  label,
  views,
  view,
  onViewChange,
}: {
  label: string
  views: PrintersView[]
  view: PrintersView
  onViewChange: (view: PrintersView) => void
}) {
  return (
    <div className='flex flex-col gap-1.5'>
      <span className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>{label}</span>
      <div
        role='group'
        aria-label={label}
        className='inline-flex items-center gap-1 rounded-lg border border-border/60 bg-muted/40 p-1'
      >
        {views.map(value => {
          const active = view === value
          return (
            <button
              key={value}
              type='button'
              aria-pressed={active}
              onClick={() => onViewChange(value)}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                active ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {VIEW_LABEL[value]}
            </button>
          )
        })}
      </div>
    </div>
  )
}
